import { useEffect, useRef } from "react";
import {
    map,
    tileLayer,
    marker,
    icon,
    LatLng,
    MapOptions,
    Map,
    MarkerOptions,
    Marker,
} from "leaflet";
import "leaflet/dist/leaflet.css";

const INITIAL_ZOOM = 16;
const MAX_ZOOM = 18;
const MIN_ZOOM = 3;

const markerIcon = icon({
    iconUrl: "/images/global/map-marker-icon.png",
    iconSize: [38, 55],
    iconAnchor: [19, 55],
    popupAnchor: [0, -50],
});

export const BusinessLocationMapOneMarkerStatic = ({
    lat,
    lng,
}: {
    lat: number;
    lng: number;
}) => {
    let myMap: {
        map?: Map;
    } = {
        map: undefined,
    };
    const businessLocationMapRef = useRef<HTMLDivElement>(null);

    const markerIcon = icon({
        iconUrl: "/images/global/map-marker-icon.png",
        iconSize: [38, 55],
        iconAnchor: [19, 55],
        popupAnchor: [0, -50],
    });

    const mapInitialSetup: MapOptions = {
        center: new LatLng(lat, lng),
        zoom: INITIAL_ZOOM,
        scrollWheelZoom: true,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
    };

    const markerSetup: MarkerOptions = {
        icon: markerIcon,
    };

    useEffect(() => {
        if (!businessLocationMapRef.current || !myMap || !lat || !lng) return;
        if (!myMap.map) {
            myMap.map = map(businessLocationMapRef.current, mapInitialSetup);
            tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
                attribution:
                    '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }).addTo(myMap.map);
        }

        marker([lat, lng], markerSetup).addTo(myMap.map);

        return () => {
            myMap.map?.remove();
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lat, lng]);

    return (
        <div
            className="rounded-3 mb-3"
            ref={businessLocationMapRef}
            style={{
                height: "14rem",
                width: "100%",
                overflow: "hidden",
                zIndex: "997",
            }}
        />
    );
};

type DraggableMapProps = {
    lat: number;
    lng: number;
    onChangeCoordinates?: (lat: number, lng: number) => void;
    mapInModalRef: React.RefObject<HTMLDivElement>;
};

export const BusinessLocationMapOneMarkerDraggable = ({
    lat,
    lng,
    onChangeCoordinates,
    mapInModalRef,
}: DraggableMapProps) => {
    const draggableMapRef = useRef<HTMLDivElement>(null);

    let myMap: {
        map?: Map;
        marker?: Marker;
    } = {
        map: undefined,
        marker: undefined,
    };

    const mapInitialSetup: MapOptions = {
        center: new LatLng(lat, lng),
        zoom: INITIAL_ZOOM,
        scrollWheelZoom: true,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
    };

    const markerSetup: MarkerOptions = {
        icon: markerIcon,
        draggable: true,
    };

    const onDragend = () => {
        if (!myMap.marker) return;

        const draggedLatLng = myMap.marker.getLatLng();

        onChangeCoordinates?.(draggedLatLng.lat, draggedLatLng.lng);

        myMap.map?.setView(draggedLatLng, MAX_ZOOM);
    };

    useEffect(() => {
        const modalEl = mapInModalRef?.current;
        if (!modalEl || !draggableMapRef.current) return;

        const handleShown = () => {
            setTimeout(() => {
                if (myMap.map) {
                    myMap.map.remove();
                }

                const createdMap = map(
                    draggableMapRef.current!,
                    mapInitialSetup
                );
                tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
                    attribution:
                        '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                }).addTo(createdMap);

                const createdMarker = marker([lat, lng], markerSetup).addTo(
                    createdMap
                );
                if (markerSetup.draggable) {
                    createdMarker.on("dragend", onDragend);
                }

                myMap.map = createdMap;
                myMap.marker = createdMarker;
            }, 50);
        };

        $(modalEl).on("shown.bs.modal", handleShown);

        return () => {
            $(modalEl).off("shown.bs.modal", handleShown);
            myMap.map?.remove();
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lat, lng]);

    return (
        <div
            className="rounded-3"
            ref={draggableMapRef}
            style={{
                height: "16rem",
                width: "100%",
                overflow: "hidden",
            }}
        />
    );
};
